import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { RequiredActionsSubscription, useHandledSubscription, useRequiredActionsSubscription } from '../../shared';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { Button } from '@mui/material';
import * as React from 'react';
import { RequiredActionOutput } from '@coverright/data-access/types/enrollment';
import _ from 'lodash';

const RequiredActionsContext = createContext<RequiredActionsSubscription['requiredActions']>([])

type ActionMap = {notistackId?: string | number, actionId: string};

// shows snacks for required actions
// show initial snack for first data array
// then show snacks for updates
export const RequiredActionsProvider = ({ children }: PropsWithChildren) => {
  const { data, restart } = useHandledSubscription(useRequiredActionsSubscription());
  const initMessageKey = useRef<string | number>();
  const prevState = useRef<any>();
  const showedActions = useRef<ActionMap[]>([]);

  const [actions, setActions] = useState<RequiredActionOutput[] | undefined>(undefined);

  useEffect(() => {
    setActions(prev => {
      if (!data?.requiredActions && prev) return prev;
      const filtered = data?.requiredActions ? _.orderBy(data?.requiredActions.filter(a => !a.done), 'createdAt', 'desc') : data?.requiredActions;
      return _.isEqual(prev, filtered) ? prev : filtered;
    });
  }, [data?.requiredActions]);

  const showSnack = (action: RequiredActionOutput) => {
    const key = enqueueSnackbar('requiredAction', {
      variant: 'requiredAction',
      requiredAction: action,
      onHide: () => closeSnack(action.id),
      preventDuplicate: false,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
      persist: true
    })

    showedActions.current.push({
      notistackId: key,
      actionId: action.id
    })
  }

  const closeSnack = (actionId: string) => {
    const action = showedActions.current.find(v => v.actionId === actionId);
    if (action && action.notistackId) {
      closeSnackbar(action.notistackId);
    }
  }

  const closeAllSnacks = () => {
    showedActions.current.forEach(v => v.actionId && closeSnack(v.actionId));
  }

  const showInitSnackbar = () => {
    closeInitSnackbar();
    initMessageKey.current = enqueueSnackbar('You have unprocessed required actions.', {
      variant: 'info',
      style: {
        backgroundColor: '#0165ad'
      },
      action: <Button sx={{color: '#fff'}}
                      onClick={() => {
                        closeInitSnackbar();
                      }}
                      size={'small'}
                      variant={'text'} color={'white'}>
        Close
      </Button>,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
      persist: true
    })
  }

  function closeInitSnackbar() {
    if (initMessageKey.current) {
      closeSnackbar(initMessageKey.current);
      initMessageKey.current = undefined;
    }
  }

  useEffect(() => {
    if (actions?.length) {
      if (prevState.current) {
        // close init message
        closeInitSnackbar();

        const newActions = actions.filter(a => !showedActions.current.some(v => v.actionId === a.id));

        let actionsToShow = newActions;

        const visibleSnacks = showedActions.current.filter(v => !!v.notistackId);

        // if more than 3 snacks already shown - show last 3 snacks and close others
        if (newActions.length + visibleSnacks.length > 3) {
          // check actions to show
          if (newActions.length > 3) {
            closeAllSnacks();
            actionsToShow = newActions.slice(0, 3);
          } else {
            const snacksToRemove = newActions.length + visibleSnacks.length - 3;
            if (snacksToRemove > 0) {
              visibleSnacks.slice(0, snacksToRemove).forEach(v => closeSnack(v.actionId));
            }
          }
        }

        actionsToShow.length && actionsToShow.forEach(showSnack)
      } else {
        // add actions to prevent snack for them in future (it was covered by initial snack)
        actions.forEach(action => {
          showedActions.current.push({
            notistackId: undefined,
            actionId: action.id
          })
        })
        showInitSnackbar();
      }
    } else {
      // close init message if all actions was resolved
      closeInitSnackbar();
    }

    //close snackbars for completed actions
    [...showedActions.current].forEach(v => (
      !actions?.some(a => a.id === v.actionId) && v.notistackId && closeSnack(v.actionId)
    ));

    prevState.current = actions;
  }, [actions]);

  useEffect(() => {
    return () => {
      closeInitSnackbar();
      closeAllSnacks();
    }
  }, []);

  const value = useMemo(() => actions?.filter(a => !a.done) || [], [actions]);

  return <RequiredActionsContext.Provider value={value}>
    {children}
  </RequiredActionsContext.Provider>;
}

export const useNotifications = () => useContext(RequiredActionsContext);
